import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import { useOnClickOutside } from '../../../../util/use-on-click-outside';
import { Product, ProductTrackingType, ProductType } from '../../../../clients/quipli-service/product/product';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Typography } from '../../../../components/typography/typography';
import Icon from '../../../../components/icons/icon';
import { debounce } from 'lodash';
import { PaginationData } from '../../../../clients/quipli-service/pagination/pagination';

export const SearchProduct = ({
  value,
  onSelect,
  productType = ProductType.Product,
  productTrackingType = undefined,
  placeholder = 'Search product',
  isForRent = undefined,
  isForSale = undefined,
}: {
  value?: Product;
  onSelect: (p: Product | undefined) => void;
  productType?: ProductType;
  productTrackingType?: ProductTrackingType;
  placeholder?: string;
  isForRent?: boolean;
  isForSale?: boolean;
}) => {
  const { searchProducts } = useAPI();

  // Refs
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [controlRef, setControlRef] = useState<HTMLDivElement | null>(null);
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(null);

  // States
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<PaginationData | undefined>();

  // Popper elements for dropdown
  const { styles, attributes } = usePopper(controlRef, dropdownRef, { placement: 'bottom', strategy: 'fixed' });

  // Search products
  const getProducts = useCallback(
    debounce(async (keyword: string, shouldAppend: boolean = false, productType: ProductType = ProductType.Product) => {
      const newProducts: Product[] = shouldAppend ? products : [];

      const search = await searchProducts({
        types: [productType],
        trackingType: productTrackingType,
        keyword,
        pageSize: 10,
        pageNumber: shouldAppend ? pagination?.nextPage || 1 : 1,
        isRental: isForRent,
        isSale: isForSale,
        sortBy: 'name',
        sortDirection: 'asc',
      });

      setProducts([...newProducts, ...search.items]);

      setPagination(search.pagination);

      setIsSearchLoading(false);
      setIsPageLoading(false);
    }, 500),
    [pagination],
  );

  // Scroll event to load more products
  const handleScroll = useCallback(() => {
    if (isSearchLoading || isPageLoading || !pagination?.nextPage || !dropdownRef) return;

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef;

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setIsPageLoading(true);
      getProducts(inputValue, true, productType);
    }
  }, [isSearchLoading, isPageLoading, pagination, dropdownRef, inputValue, getProducts]);

  // Close dropdown when clicked outside
  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  // Resize observer to get wrapper width
  useEffect(() => {
    if (!wrapperRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setWrapperWidth(wrapperRef.current?.scrollWidth ?? 0);
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [wrapperRef]);

  // Scroll event listener
  useEffect(() => {
    if (!dropdownRef) return;

    handleScroll();

    dropdownRef.addEventListener('scroll', handleScroll);
    return () => dropdownRef.removeEventListener('scroll', handleScroll);
  }, [dropdownRef, handleScroll]);

  // Set input if there is a selected option
  useEffect(() => {
    setInputValue(value?.name || '');
  }, [value]);

  // Get products on input change
  useEffect(() => {
    setIsSearchLoading(true);
    getProducts(inputValue, false, productType);
  }, [inputValue, productType]);

  // Focus input on open
  useEffect(() => {
    isOpen ? inputRef.current?.focus() : inputRef.current?.blur();
  }, [isOpen]);

  return (
    <div ref={wrapperRef}>
      <div
        ref={setControlRef}
        data-cy="search_products"
        className={classNames(
          'flex items-center gap-x-[6px] bg-quipli-white border border-solid border-grey-300 rounded-[0.5rem] cursor-pointer',
          {
            '!border-primary-500': isOpen,
          },
        )}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <div className="ml-[10px] flex items-center justify-center">
          <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
        </div>

        <input
          type="text"
          className="cursor-pointer !outline-none !border-0 !border-none grow text-sm"
          placeholder={placeholder}
          ref={inputRef}
          value={inputValue}
          onChange={({ target }) => setInputValue(target.value)}
        />

        {value && (
          <div
            className="flex items-center justify-center mr-1"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(undefined);
              setInputValue('');
            }}
          >
            <Icon icon="Close" size="medium" />
          </div>
        )}

        {isOpen && (
          <div
            ref={setDropdownRef}
            className="bg-quipli-white border border-solid border-grey-100 rounded-[9px] shadow-md mt-2 p-1 overflow-y-auto"
            style={{ ...styles.popper, zIndex: 5, width: `${wrapperWidth}px`, maxHeight: '238px' }}
            {...attributes.popper}
          >
            <ul className="list-none m-0 p-0" data-cy="product-list-wrapper">
              {isSearchLoading ? (
                <StaticItem text="Loading ..." />
              ) : products.length === 0 ? (
                <StaticItem text="No products" />
              ) : (
                <>
                  {products.map((product) => (
                    <li
                      key={`add-group-product-${product.id}`}
                      data-group-wrapper-id={product.id}
                      className="border border-grey-200 rounded-[6px]"
                    >
                      <div
                        key={`add-product-${product.id}}`}
                        className={classNames(
                          'cursor-pointer rounded-[6px] text-grey-800 py-2 px-3 hover:bg-grey-100 select-group-child',
                        )}
                        data-group-id={product.id}
                        onClick={() => {
                          onSelect(product);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <Typography type="text-sm" className="text-quipli-black-800 flex items-center gap-y-10">
                            {product.name}
                            {!product.isActive && <Icon icon="EyeOff" size="medium" />}
                          </Typography>
                        </div>
                      </div>
                    </li>
                  ))}

                  {isPageLoading && <StaticItem text="Loading ..." />}
                </>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const StaticItem = ({ text }: { text: string }) => (
  <li className="text-grey-800 py-2 px-3 text-center">
    <Typography type="text-sm">{text}</Typography>
  </li>
);
